import { captureException } from '@sentry/nextjs';
import { AxiosError } from 'axios';
import { createPost } from 'components/post/post-helpers';
import { PostType } from 'components/post/post-types';
import ReactSquadModalAd from 'components/reactsquadad/react-squad-modal-ad';
import { htmlToText } from 'html-to-text';
import { findCategory, getAllCategories, getCategory } from 'lib/db/category';
import { getCategoryPosts, getPostsByTag } from 'lib/db/post';
import { Category } from 'lib/db/types';
import { GetStaticPaths, GetStaticProps } from 'next';
import Link from 'next/link';
import { NextSeo } from 'next-seo';

import { Button } from '../../components/button/button-component';
import LayoutComponent from '../../components/layout/layout-component';
import { Post as PostComponent } from '../../components/post/post-component';
import SidebarComponent from '../../components/sidebar/sidebar-component';
import { Typography } from '../../components/typography/typography-component';
import { stripHtmlContent } from '../../lib/utils/strip-html';

export default function CategoryPage({
  posts,
  latestPosts,
  canonicalUrl,
  category,
  hasMorePost,
}: {
  posts: PostType[];
  latestPosts: PostType[];
  category: Category;
  canonicalUrl: string;
  hasMorePost: boolean;
}) {
  if (!category) {
    return;
  }
  const { yoast_head_json: seo, name, description, slug } = category;

  const [firstPost, ...restOfPosts] = posts;

  const SEO = {
    title: htmlToText(stripHtmlContent(seo?.title ?? '') || name || ''),
    description: seo?.description || description || '',
    openGraph: {
      type: seo?.og_type || '',
      url: canonicalUrl,
      siteName: seo?.og_site_name || '',
      title: seo?.og_title || name || '',
      description: seo?.og_description || description || '',
      images: [
        {
          url: `${process.env.NEXT_PUBLIC_SITE_URL}/assets/earlynode-meta.png`,
          alt: name || '',
        },
      ],
    },
    canonicalUrl,
  };

  return (
    <LayoutComponent>
      <NextSeo {...SEO} />
      <div className="mx-auto mt-5 flex w-full flex-col py-5 pb-9 align-top md:container lg:mt-10 lg:flex-row">
        <div className="mx-auto w-full px-4 lg:w-[95ch]">
          <div className="mb-6">
            <Typography variant="h1" className="text-left text-[1.8rem]">
              {`Featured Posts in ${htmlToText(name || '')}`}
            </Typography>
            {description && (
              <Typography variant="paragraph" className="mt-4 text-left">
                {description}
              </Typography>
            )}
          </div>

          <hr className="my-4" />

          <div className="mx-4 mb-12 sm:mx-0">
            {posts.length > 0 ? (
              <>
                {firstPost && (
                  <PostComponent
                    title={firstPost.title ?? ''}
                    img={firstPost.img ?? ''}
                    link={firstPost.link}
                    variant="featured"
                    description={htmlToText(
                      stripHtmlContent(firstPost.description ?? ''),
                    )}
                  />
                )}
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {restOfPosts.map((post, index) => (
                    <PostComponent
                      key={index}
                      title={post.title ?? ''}
                      img={post.img ?? ''}
                      link={post.link}
                      variant="count"
                      description={htmlToText(
                        stripHtmlContent(post.description ?? ''),
                      )}
                      count={`${index > 10 ? '' : '0'}${index + 1}`}
                    />
                  ))}
                </div>
              </>
            ) : (
              <Typography variant="paragraph" className="w-full text-left">
                No featured posts found
              </Typography>
            )}
          </div>

          {hasMorePost && (
            <Link className="mb-5" href={`/${slug}/page/1`}>
              <Button>View All Posts</Button>
            </Link>
          )}
        </div>

        <div className="mt-10 w-full lg:mt-0 lg:w-1/3">
          <SidebarComponent
            posts={latestPosts}
            categorySlug={slug ?? ''}
            postTitle={`Newest Articles of ${name}`}
          />
        </div>
      </div>
      <ReactSquadModalAd />
    </LayoutComponent>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  const allCategories = await getAllCategories();

  return {
    paths: allCategories.map(node => `/${node?.slug}`) || [],
    fallback: false,
  };
};

// This is a temporary fix for the error we're getting on the homepage.
// The API seems to be NOT returning an array, but an object and therefore,
// .map doesn't work.
function capitalizeFirstLetter(input: string): string {
  if (!input) return '';
  return input.charAt(0).toUpperCase() + input.slice(1);
}

const createDefaultProps = (slug: string) => ({
  hasMorePost: false,
  posts: [],
  latestPosts: [],
  category: {
    yoast_head_json: null,
    name: capitalizeFirstLetter(slug),
    description: '',
    slug,
  },
  canonicalUrl: `${process.env.NEXT_PUBLIC_SITE_URL}/${slug}`,
});

export const getStaticProps: GetStaticProps = async ({ params }) => {
  // This shouldn't stay in the code base, we should remove it at some point,
  // once we figure out what's going wrong.
  const debugPostData: any = {};

  const { category } = params || {};

  if (typeof category !== 'string') {
    return { notFound: true };
  }

  try {
    const categories = await findCategory({ slug: category });

    if (categories?.length === 0) {
      captureException(new Error(`Category ${category} not found`), {
        tags: {
          potential404bug: 'true',
        },
      });
      return { props: createDefaultProps(category) };
    }

    const categoryId = categories[0]?.id;

    if (!categoryId) {
      captureException(
        new Error(`Category ID is undefined ${category} ${categoryId}`),
        {
          tags: {
            potential404bug: 'true',
          },
        },
      );
    }

    const foundCategory = await getCategory({ id: categoryId });

    const posts = await getPostsByTag({
      categoryId: foundCategory.id,
      limit: 5,
      tag: 'featured-category',
    });
    debugPostData['posts'] = posts;

    const categoryPosts = await getCategoryPosts({
      categoryId: foundCategory.id,
      limit: 1,
      page: 1,
    });
    debugPostData['categoryPosts'] = categoryPosts;

    const latestPosts = await getCategoryPosts({
      categoryId: foundCategory.id,
      page: 1,
      limit: 5,
    });
    debugPostData['latestPosts'] = latestPosts;

    const canonicalUrl = `${process.env.NEXT_PUBLIC_SITE_URL}/${foundCategory.slug}`;

    return {
      props: {
        hasMorePost: categoryPosts.data.length > 0,
        posts: posts?.map(createPost) ?? [],
        latestPosts: latestPosts?.data?.map(createPost) ?? [],
        category: {
          yoast_head_json: foundCategory?.yoast_head_json ?? null,
          name: foundCategory?.name ?? '',
          description: foundCategory?.description ?? '',
          slug: foundCategory?.slug ?? '',
        },
        canonicalUrl,
      },
      revalidate: 600,
    };
  } catch (error) {
    captureException(error, {
      extra: {
        path: `getStaticProps: /${category}`,
        debugPostData,
        apiError: error instanceof AxiosError ? error?.response?.data : false,
        tags: {
          potential404bug: 'true',
        },
      },
    });
    return { props: createDefaultProps(category) };
  }
};
